import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { api } from '@/lib/api';
import { closeSocket, getSocket } from '@/lib/ws';
import { surfacePrincipal, useAuthStore } from './authStore';
import type {
  AuthPrincipal,
  ClassCodeLoginResponse,
  ClassLoginPollResponse,
  ClassLoginRequestCreateResponse,
  KidDeviceIdentity,
  KidHandoffCreateResponse,
  KidHandoffPreviewResponse,
  KidHandoffRedeemResponse,
  KidLoginResponse,
  MeResponse,
  PrincipalKind,
  SchoolKidLoginResponse,
  UnifiedSignInResponse,
  VerifyOtpResponse,
} from './types';
import { acceptSignIn } from './unifiedSignIn';

// Normalise the backend `/auth/me` shape into our discriminated AuthPrincipal.
export function normaliseMe(raw: MeResponse): AuthPrincipal {
  if (raw.role === 'kid' && raw.kid) {
    return {
      kind: 'kid',
      sub: raw.kid.id,
      nickname: raw.kid.nickname,
      avatar_id: raw.kid.avatar_id,
      // School account-only records can omit age; never invent a demographic.
      age: raw.kid.age ?? undefined,
      family_id: raw.kid.family_id,
      // Walk-in (unclaimed) kids get the restricted Learn surface (§5.2); the
      // claim code renders on their profile so a parent can claim them later.
      is_ephemeral: raw.kid.is_ephemeral,
      claim_code: raw.kid.claim_code,
      // Student self-signup (student-self-signup-prd.md §6). An older backend
      // omits these; the kid then reads as a normal parent-managed account.
      account_mode: raw.kid.account_mode ?? 'managed',
      family_kind: raw.kid.family_kind ?? null,
      login_email: raw.kid.login_email ?? null,
      has_password: raw.kid.has_password ?? false,
    };
  }
  if (!raw.user) {
    throw new Error('Malformed /auth/me response');
  }
  return {
    kind: 'user',
    sub: raw.user.id,
    email: raw.user.email,
    display_name: raw.user.display_name,
    phone: raw.user.phone,
    role: raw.user.role,
    family_id: raw.user.family_id,
    has_password: raw.user.has_password ?? false,
  };
}

// `/auth/me` is fetched per principal kind so the parent and kid sessions are
// cached independently. Defaults to the surface you're on (`/learn/*` = kid),
// which is correct for every page since the two surfaces are route-segregated;
// ProtectedRoute/RootPage pass an explicit kind.
export function useMe(kind: PrincipalKind = surfacePrincipal(), enabled = true) {
  const token = useAuthStore((s) => s.tokens[kind]);
  return useQuery<AuthPrincipal>({
    queryKey: ['auth', 'me', kind],
    queryFn: async () => normaliseMe(await api<MeResponse>('/auth/me', { principal: kind })),
    enabled: enabled && token !== null,
    staleTime: 60_000,
  });
}

// ── Parent OTP flow ─────────────────────────────────────────────────────────

export async function requestOtp(email: string): Promise<void> {
  await api<void>('/auth/request-otp', {
    method: 'POST',
    body: { email, role_hint: 'parent' },
    skipAuthRefresh: true,
  });
}

export interface ParentEmailStatus {
  registered: boolean;
  has_password: boolean;
}

export async function checkParentEmail(email: string): Promise<ParentEmailStatus> {
  return api<ParentEmailStatus>('/auth/parent-email-status', {
    method: 'POST',
    body: { email },
    skipAuthRefresh: true,
  });
}

// Unified email sign-in (D-SSU-24): a student email answers with the student's
// kid session instead of 409 STUDENT_ACCOUNT_EMAIL. `acceptSignIn` stores the
// token in the slot the server names; callers branch with `isKidSignIn(res)`
// and route with `signInLandingPath(res)`.
export async function verifyOtp(
  email: string,
  code: string,
  purpose: 'parent' | 'teacher_app' = 'parent',
): Promise<UnifiedSignInResponse> {
  const res = await api<UnifiedSignInResponse>('/auth/verify-otp', {
    method: 'POST',
    body: { email, code, purpose },
    skipAuthRefresh: true,
  });
  acceptSignIn(res);
  return res;
}

// ── Parent email+password login (auth-system-prd §4.8) ──────────────────────
// Optional alternative to OTP for parents who have opted into a password. Same
// token envelope + post-login handling as verifyOtp. OTP stays the recovery path,
// so a wrong/forgotten password just falls back to "email me a code".

export async function loginWithPassword(
  email: string,
  password: string,
): Promise<UnifiedSignInResponse> {
  const res = await api<UnifiedSignInResponse>('/auth/login-password', {
    method: 'POST',
    body: { email, password },
    skipAuthRefresh: true,
  });
  acceptSignIn(res);
  return res;
}

// Set/rotate the current parent's password (authenticated session, min 8 chars).
// Surfaced from /portal/settings. Backend hashes with bcrypt; plaintext never
// persisted client-side.
export async function setPassword(password: string): Promise<void> {
  const res = await api<VerifyOtpResponse>('/auth/set-password', {
    method: 'POST',
    body: { password },
  });
  // The backend revokes every old session on password rotation, then returns a
  // fresh session for this browser only. Replace the now-invalid access token
  // before Settings refreshes /auth/me.
  useAuthStore.getState().setToken('user', res.access_token);
}

// ── Kid family-code login ───────────────────────────────────────────────────

export async function kidLogin(
  familyCode: string,
  nickname: string,
  pin: string,
): Promise<KidLoginResponse> {
  const res = await api<KidLoginResponse>('/auth/kid-login', {
    method: 'POST',
    body: { family_code: familyCode, nickname, pin },
    skipAuthRefresh: true,
  });
  useAuthStore.getState().setToken('kid', res.access_token);
  useAuthStore.getState().setBootstrapped(true);
  // Trigger WS connect with the new kid token
  setTimeout(() => getSocket('kid'), 0);
  return res;
}

// ── Institution classroom login (institution-prd-p1-foundation §5.5) ────────

// An institution student has no parent account and no family code: the school
// hands out a printed card with the school's code, the child's login id and a
// 4-digit PIN. The backend normalises the code to upper case and the login id
// to lower case, so a child typing on a classroom tablet cannot fail on
// capitalisation — nothing is normalised here beyond trimming.
export async function schoolKidLogin(
  institutionShortCode: string,
  studentLoginId: string,
  pin: string,
): Promise<SchoolKidLoginResponse> {
  const res = await api<SchoolKidLoginResponse>('/auth/kid/school-login', {
    method: 'POST',
    body: {
      institution_short_code: institutionShortCode.trim(),
      student_login_id: studentLoginId.trim(),
      pin,
    },
    skipAuthRefresh: true,
  });
  return acceptKidSession(res);
}

/**
 * Institution school sign-in (SIR-IDN-02): trade the one-time ticket from the SSO callback for the
 * same kid session a PIN sign-in returns. The API binds it to this page's Origin and, for school
 * sign-in, to the `clientNonce` this tab kept when it started (an LMS launch ticket has none).
 */
export async function exchangeStudentSsoTicket(
  ticket: string,
  clientNonce?: string,
): Promise<SchoolKidLoginResponse> {
  const res = await api<SchoolKidLoginResponse & { audience: 'staff' | 'student' }>(
    '/institution-auth/sso/exchange',
    {
      method: 'POST',
      body: { ticket, ...(clientNonce ? { client_nonce: clientNonce } : {}) },
      skipAuthRefresh: true,
    },
  );
  if (res.audience !== 'student') throw new Error('Not a student sign-in');
  return acceptKidSession(res);
}

function acceptKidSession<T extends KidLoginResponse>(res: T): T {
  useAuthStore.getState().setToken('kid', res.access_token);
  useAuthStore.getState().setBootstrapped(true);
  setTimeout(() => getSocket('kid'), 0);
  return res;
}

export async function createKidHandoff(
  kidId: string,
  rememberDevice: boolean,
): Promise<KidHandoffCreateResponse> {
  return api<KidHandoffCreateResponse>(`/auth/parent/kids/${kidId}/handoff`, {
    method: 'POST',
    body: { remember_device: rememberDevice },
    principal: 'user',
  });
}

export async function previewKidHandoff(token: string): Promise<KidHandoffPreviewResponse> {
  return api<KidHandoffPreviewResponse>('/auth/kid-handoff/preview', {
    method: 'POST',
    body: { token },
    skipAuthRefresh: true,
  });
}

export async function redeemKidHandoff(token: string): Promise<KidHandoffRedeemResponse> {
  const res = await api<KidHandoffRedeemResponse>('/auth/kid-handoff/redeem', {
    method: 'POST',
    body: { token },
    skipAuthRefresh: true,
  });
  return acceptKidSession(res);
}

export async function previewKidDevice(deviceHint: string): Promise<KidDeviceIdentity> {
  const res = await api<{ kid: KidDeviceIdentity }>('/auth/kid-device/preview', {
    method: 'POST',
    body: { device_hint: deviceHint },
    skipAuthRefresh: true,
  });
  return res.kid;
}

export async function kidDeviceLogin(deviceHint: string, pin: string): Promise<KidLoginResponse> {
  const res = await api<KidLoginResponse>('/auth/kid-device/login', {
    method: 'POST',
    body: { device_hint: deviceHint, pin },
    skipAuthRefresh: true,
  });
  return acceptKidSession(res);
}

export async function revokeKidDevices(kidId: string): Promise<number> {
  const res = await api<{ revoked: number }>(
    `/auth/parent/kids/${kidId}/trusted-devices/revoke-all`,
    { method: 'POST', principal: 'user' },
  );
  return res.revoked;
}

// A signed-in parent can open one of their own kids on this browser without
// re-entering the family code, nickname or PIN. The backend family-scopes the
// request and writes only the dedicated kid refresh cookie, so the adult portal
// session remains available in parallel.
export function useParentKidLogin() {
  const qc = useQueryClient();
  return async (kidId: string): Promise<KidLoginResponse> => {
    const res = await api<KidLoginResponse>(`/auth/parent/kids/${kidId}/login`, {
      method: 'POST',
      principal: 'user',
    });
    useAuthStore.getState().setToken('kid', res.access_token);
    useAuthStore.getState().setBootstrapped(true);
    qc.removeQueries({ queryKey: ['auth', 'me', 'kid'] });
    setTimeout(() => getSocket('kid'), 0);
    return res;
  };
}

// ── Kid one-shot class-code login ───────────────────────────────────────────

export async function classCodeLogin(
  classCode: string,
  displayNickname: string,
): Promise<ClassCodeLoginResponse> {
  const res = await api<ClassCodeLoginResponse>('/auth/class-code-login', {
    method: 'POST',
    body: { class_code: classCode, display_nickname: displayNickname },
    skipAuthRefresh: true,
  });
  useAuthStore.getState().setToken('kid', res.access_token);
  useAuthStore.getState().setBootstrapped(true);
  // Trigger WS connect with the new kid token
  setTimeout(() => getSocket('kid'), 0);
  return res;
}

// Staff-precreated walk-in login. The code is already bound to a temporary kid
// and class, so the child enters only this one-time credential (or scans its QR).
export async function temporaryClassLogin(code: string): Promise<KidLoginResponse> {
  const res = await api<KidLoginResponse>('/class-login/temporary/redeem', {
    method: 'POST',
    body: { code },
    skipAuthRefresh: true,
  });
  return acceptKidSession(res);
}

// ── Kid class login with teacher approval (auth-system-prd §5.3) ────────────

// Creates a pending login request; the kid is unauthenticated until the teacher
// approves, so the returned `secret` is the only credential for polling.
export async function createClassLoginRequest(
  classCode: string,
  typedName: string,
): Promise<ClassLoginRequestCreateResponse> {
  return api<ClassLoginRequestCreateResponse>('/class-login/requests', {
    method: 'POST',
    body: { class_code: classCode.toUpperCase(), typed_name: typedName },
    skipAuthRefresh: true,
  });
}

// One poll tick. On `approved` the response carries the tokens (and the backend
// set the kid refresh cookie on this response) — store them and open the socket,
// exactly like the other kid logins.
export async function pollClassLoginRequest(
  requestId: string,
  secret: string,
): Promise<ClassLoginPollResponse> {
  const res = await api<ClassLoginPollResponse>(`/class-login/requests/${requestId}/poll`, {
    method: 'POST',
    body: { secret },
    skipAuthRefresh: true,
  });
  if (res.status === 'approved') {
    useAuthStore.getState().setToken('kid', res.access_token);
    useAuthStore.getState().setBootstrapped(true);
    setTimeout(() => getSocket('kid'), 0);
  }
  return res;
}

// ── Logout ──────────────────────────────────────────────────────────────────

// Logs out a single principal so the other session stays alive. Defaults to the
// current surface's principal.
export function useLogout() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return async (kind: PrincipalKind, everywhere = false) => {
    try {
      await api<void>(everywhere ? '/auth/logout-everywhere' : '/auth/logout', {
        method: 'POST',
        principal: kind,
      });
    } catch {
      // ignore — clearing client state regardless
    }
    useAuthStore.getState().clearToken(kind);
    closeSocket(kind);
    qc.removeQueries({ queryKey: ['auth', 'me', kind] });
    // Navigate explicitly after an intentional sign-out. A browser can hold a
    // parent and kid session together. On an allow-listed studio route, only
    // clearing the kid token reclassified the still-mounted workspace as a
    // parent-opened studio instead of completing the kid sign-out transition.
    navigate(kind === 'kid' ? '/learn/login' : '/portal/login', { replace: true });
  };
}
